import { useState } from 'react';
import { Heading, Text, Box, Flex, Button, Icon, Dropdown, Divider, Tooltip, Search } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { Bullet, Remove } from '@vibe/icons';
import { DashboardActivityChart } from './DashboardActivityChart';
import { ActivityLog } from './ActivityLog';
import { type FrontendAnalyticsData, getActivityChartData, summarizedActivityChartData } from '../services/analytics';

type DashboardStatsProps = {
  screenMode: 'small' | 'medium' | 'large';
  analyticsData: FrontendAnalyticsData;
};

export function DashboardStats({ screenMode, analyticsData }: DashboardStatsProps) {
  const [selectedTimerange, setSelectedTimerange] = useState<'30_days' | '7_days'>('30_days');
  const [selectedEventType, setSelectedEventType] = useState<'all' | 'auth' | 'comment'>('all');
  const [tab, setTab] = useState<'activity_chart' | 'activity_log'>('activity_chart');
  const chartData = getActivityChartData(analyticsData, selectedTimerange === '7_days' ? 7 : 30);
  const summarizedChartData = summarizedActivityChartData(chartData);

  const optionsTimerange = [
    {
      value: '7_days',
      label: 'Last 7 days',
    },
    {
      value: '30_days',
      label: 'Last 30 days',
    },
  ];

  const optionsEventType = [
    {
      value: 'all',
      label: 'All events',
    },
    {
      value: 'auth',
      label: 'Logins',
    },
    {
      value: 'comment',
      label: 'Comments',
    },
  ];

  //console.log('analyticsData', analyticsData);

  return (
    <Flex
      direction="column"
      align="start"
      justify="start"
      gap={0}
      style={{
        flex: 1,
        maxWidth: screenMode === 'small' ? '100%' : 'calc(100vw - 444px)',
      }}
    >
      <Box
        style={{
          zIndex: '100',
          position: 'relative',
          backgroundColor: '#fff',
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
        }}
      >
        <Flex direction="column" justify="start" align="start" gap={16}>
          <Flex gap={0}>
            <Button
              insetFocus
              leftFlat
              rightFlat
              preventClickAnimation
              kind="secondary"
              size="small"
              style={{
                pointerEvents: tab === 'activity_chart' ? 'none' : 'auto',
                marginRight: '-1px',
                padding: '6px 14px',
                height: 'auto',
                borderBottomColor: tab === 'activity_chart' ? 'transparent' : '',
                borderTopLeftRadius: '4px',
                backgroundColor: tab !== 'activity_chart' ? '#f6f7fb' : 'transparent',
              }}
              onClick={() => {
                setTab('activity_chart');
              }}
            >
              Activity
            </Button>
            <Button
              insetFocus
              leftFlat
              rightFlat
              preventClickAnimation
              kind="secondary"
              size="small"
              style={{
                pointerEvents: tab === 'activity_log' ? 'none' : 'auto',
                padding: '6px 14px',
                height: 'auto',
                borderBottomColor: tab === 'activity_log' ? 'transparent' : '',
                borderTopRightRadius: '4px',
                backgroundColor: tab !== 'activity_log' ? '#f6f7fb' : 'transparent',
              }}
              onClick={() => {
                setTab('activity_log');
              }}
            >
              Events
            </Button>
          </Flex>
        </Flex>
      </Box>

      {tab === 'activity_chart' ? (
        <Box
          backgroundColor="primaryBackgroundColor"
          borderColor="uiBorderColor"
          border
          shadow="xs"
          style={{
            zIndex: '50',
            position: 'relative',
            marginTop: '-1px',
            width: '100%',
            borderTopRightRadius: '4px',
            borderBottomRightRadius: '4px',
            borderBottomLeftRadius: '4px',
          }}
        >
          <Box padding="xl" paddingTop="large" paddingEnd="large" paddingBottom="medium">
            <Flex direction="column" justify="start" align="start" gap={16}>
              <Flex gap={10} style={{ width: '100%' }}>
                <Heading type="h3" weight="bold" align="start">
                  Activity in your Client Portal
                </Heading>
                <Box style={{ marginLeft: 'auto', minWidth: 0, flexShrink: 0 }}>
                  <Flex gap={24}>
                    <Flex gap={8}>
                      <Tooltip content="Unique client logins per day" position="left" showOnDialogEnter={false}>
                        <Flex gap={4}>
                          <div style={{ position: 'relative', lineHeight: '12px', color: '#0073ea' }}>
                            <Icon
                              icon={Remove}
                              size={20}
                              useCurrentColor
                              style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate3d(-50%,-50%,0)' }}
                            />
                            <Icon icon={Bullet} size={24} useCurrentColor />
                          </div>
                          <Text type="text2" color="secondary">
                            Logins
                          </Text>
                        </Flex>
                      </Tooltip>
                      <Tooltip content="Operations processed per day" position="left" showOnDialogEnter={false}>
                        <Flex gap={4}>
                          <div style={{ position: 'relative', lineHeight: '12px', color: '#d0d4e4' }}>
                            <Icon
                              icon={Remove}
                              size={20}
                              useCurrentColor
                              style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate3d(-50%,-50%,0)' }}
                            />
                            <Icon icon={Bullet} size={24} useCurrentColor />
                          </div>
                          <Text type="text2" color="secondary">
                            Requests
                          </Text>
                        </Flex>
                      </Tooltip>
                    </Flex>
                    <div style={{ width: '130px', minWidth: '130px' }}>
                      <Dropdown
                        searchable
                        size="small"
                        placeholder="Select"
                        value={dropdownValue(optionsTimerange.find((opt) => opt.value === selectedTimerange))}
                        options={optionsTimerange}
                        clearable={false}
                        onChange={(value) => {
                          const actualValue = typeof value === 'string' ? value : (value as { value: string }).value;

                          setSelectedTimerange(actualValue as '30_days' | '7_days');
                        }}
                      />
                    </div>
                  </Flex>
                </Box>
              </Flex>
              <div
                style={{
                  width: '100%',
                  // Define height based on content
                  height: screenMode === 'small' ? '230px' : '175px',
                }}
              >
                <DashboardActivityChart chartData={chartData} screenMode={screenMode} />
              </div>
            </Flex>
          </Box>

          <Box>
            <Divider withoutMargin />
            <Flex direction="row">
              <Box padding="medium" paddingStart="large" paddingEnd="large">
                <Flex gap={8} direction="row" align="center">
                  <Text type="text1" color="primary" align="center" ellipsis={false}>
                    {summarizedChartData.loginsCount.toLocaleString()}
                  </Text>
                  <Text type="text3" color="secondary" align="center">
                    Total client logins
                  </Text>
                </Flex>
              </Box>
              <div style={{ alignSelf: 'stretch' }}>
                <Divider direction="vertical" withoutMargin />
              </div>
              <Box padding="medium" paddingStart="large" paddingEnd="large">
                <Flex gap={8} direction="row" align="center">
                  <Text type="text1" color="primary" align="center" ellipsis={false}>
                    {(summarizedChartData.successRequestsCount + summarizedChartData.errorRequestsCount).toLocaleString()}
                  </Text>
                  <Text type="text3" color="secondary" align="center">
                    Total requests
                  </Text>
                </Flex>
              </Box>
              <div style={{ alignSelf: 'stretch' }}>
                <Divider direction="vertical" withoutMargin />
              </div>
              <Box padding="medium" paddingStart="large" paddingEnd="large">
                <Flex gap={8} direction="row" align="center">
                  <Text type="text1" color="primary" align="center" ellipsis={false}>
                    {(() => {
                      const success = summarizedChartData.successRequestsCount;
                      const error = summarizedChartData.errorRequestsCount;
                      const total = success + error;
                      return total === 0 ? '0%' : `${((success / total) * 100).toFixed(2)}%`;
                    })()}
                  </Text>
                  <Text type="text3" color="secondary" align="center">
                    Successful requests
                  </Text>
                </Flex>
              </Box>
              <div style={{ alignSelf: 'stretch' }}>
                <Divider direction="vertical" withoutMargin />
              </div>
              <Box padding="medium" paddingStart="large" paddingEnd="large">
                <Flex gap={8} direction="row" align="center">
                  <Text type="text1" color="primary" align="center" ellipsis={false}>
                    {(() => {
                      const success = summarizedChartData.successRequestsCount;
                      const error = summarizedChartData.errorRequestsCount;
                      const total = success + error;
                      return total === 0 ? '0%' : `${((error / total) * 100).toFixed(2)}%`;
                    })()}
                  </Text>
                  <Text type="text3" color="secondary" align="center">
                    Requests with errors
                  </Text>
                </Flex>
              </Box>
            </Flex>
          </Box>
        </Box>
      ) : null}

      {tab === 'activity_log' ? (
        <Box
          backgroundColor="primaryBackgroundColor"
          borderColor="uiBorderColor"
          border
          shadow="xs"
          style={{
            zIndex: '50',
            position: 'relative',
            marginTop: '-1px',
            width: '100%',
            borderTopRightRadius: '4px',
            borderBottomRightRadius: '4px',
            borderBottomLeftRadius: '4px',
          }}
        >
          <Box padding="xl" paddingTop="large" paddingEnd="large" paddingBottom="medium">
            <Flex direction="column" justify="start" align="start" gap={16}>
              <Flex gap={10} style={{ width: '100%' }}>
                <Heading type="h3" weight="bold" align="start">
                  Events in your Client Portal
                </Heading>
                <Box style={{ marginLeft: 'auto', minWidth: 0, flexShrink: 0 }}>
                  <Flex gap={16}>
                    <div style={{ width: '130px', minWidth: '130px' }}>
                      <Dropdown
                        searchable
                        size="small"
                        placeholder="Select"
                        value={dropdownValue(optionsEventType.find((opt) => opt.value === selectedEventType))}
                        options={optionsEventType}
                        clearable={false}
                        onChange={(value) => {
                          const actualValue = typeof value === 'string' ? value : (value as { value: string }).value;

                          setSelectedEventType(actualValue as 'all' | 'auth' | 'comment');
                        }}
                      />
                    </div>
                    {/* 
                    <div style={{ width: '130px', minWidth: '130px' }}>
                      <Search 
                        id="search-events"
                        className="custom-events-search"
                        inputAriaLabel="Search events"
                        placeholder="Search"
                        size="small"
                      />
                    </div>
                    */}
                  </Flex>
                </Box>
              </Flex>
              <div
                style={{
                  width: '100%',
                  // Define height based on content
                  height: screenMode === 'small' ? '230px' : '230px',
                }}
              >
                <ActivityLog recentActivityLog={analyticsData.recentActivityLog} selectedEventType={selectedEventType} />
              </div>
            </Flex>
          </Box>
        </Box>
      ) : null}
    </Flex>
  );
}
